import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthService } from '../../auth/auth.service';
import { LoggerService } from '../../services/logger.service';
import { environment } from '../../../environments/environment';

/**
 * ExportJobTracker
 * ----------------
 * Servicio singleton que gestiona la "vida" de los jobs asíncronos de
 * exportación en el lado cliente. Mantiene un Map<jobId, AsyncJobState>
 * expuesto como Signal y abre/cierra las conexiones SSE contra el backend
 * (`/api/exports/jobs/:id/stream`).
 *
 * Responsabilidades:
 *   - Suscribirse al SSE en cuanto el `ExportPanel` encola un job.
 *   - Aplicar los eventos `snapshot/progress/partial/success/error/cancelled`
 *     al estado y notificar a la UI vía Signals.
 *   - Reconectar con backoff exponencial si el stream cae (hasta 5 intentos).
 *   - Cancelar un job (POST al backend) y olvidar el estado tras cierre.
 *
 * IMPORTANTE: este servicio NO inicia los jobs — eso lo hace el `ExportService`
 * con `startAsyncJob(...)` y le pasa el `jobId` al tracker. La separación es
 * deliberada: encolar y trackear son fases distintas (encolar es síncrono;
 * trackear es un stream persistente).
 *
 * No persiste entre sesiones: si el usuario cierra la pestaña, el job sigue
 * corriendo en backend (visible más adelante en "Mis exportaciones", Punto 6.C),
 * pero el tracker arranca limpio.
 *
 * @author Marcos Cordo
 * @version 06/06/2026_1
 */

/** Estados terminales y no-terminales que un job puede atravesar. */
export type JobStatus =
  | 'queued'
  | 'running'
  | 'partial'
  | 'success'
  | 'failed'
  | 'cancelled';

/** Estado de un job tal y como lo expone el tracker a la UI. */
export interface AsyncJobState {
  jobId: string;
  exporterId: string;             // 'jira' | 'gitlab' | ...
  exporterLabel: string;          // 'Jira', 'GitLab' (display)
  status: JobStatus;
  progress: number;               // 0..1
  message?: string;
  createdCount?: number;
  failedCount?: number;
  externalUrl?: string;
  error?: string;
  startedAt: number;              // ms epoch
}

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

@Injectable({ providedIn: 'root' })
export class ExportJobTracker {
  private auth = inject(AuthService);
  private logger = inject(LoggerService);

  /** Estado interno: jobId → AsyncJobState. */
  private readonly jobsMap = signal<Map<string, AsyncJobState>>(new Map());

  /** Lista de jobs ordenada por createdAt asc (estable para la UI). */
  readonly jobs = computed(() =>
    Array.from(this.jobsMap().values()).sort((a, b) => a.startedAt - b.startedAt)
  );

  /** Conteo de jobs todavía vivos (queued | running). */
  readonly activeCount = computed(
    () => this.jobs().filter(j => j.status === 'queued' || j.status === 'running').length
  );

  /** ¿Hay al menos un job vivo? */
  readonly hasActive = computed(() => this.activeCount() > 0);

  /** Controladores Abort por jobId — uno por conexión SSE viva. */
  private connections = new Map<string, AbortController>();

  // ==========================================================================
  // API PÚBLICA
  // ==========================================================================

  /**
   * Empieza a trackear un job recién encolado. Abre el stream SSE en background.
   *
   * @returns Promise que resuelve con el estado FINAL del job
   *          (success | failed | cancelled). El caller la usa para mostrar el
   *          Toast resumen y luego invoca `forget(jobId)`.
   */
  track(jobId: string, exporterId: string, exporterLabel: string): Promise<AsyncJobState> {
    if (this.connections.has(jobId)) {
      this.logger.warn(`[ExportJobTracker] jobId ${jobId} ya está siendo trackeado.`);
      const existing = this.jobsMap().get(jobId);
      return Promise.resolve(existing!);
    }

    this.upsertJob({
      jobId,
      exporterId,
      exporterLabel,
      status: 'queued',
      progress: 0,
      startedAt: Date.now()
    });

    return new Promise<AsyncJobState>((resolve) => {
      void this.openStreamWithRetry(jobId, resolve);
    });
  }

  /**
   * Solicita la cancelación cooperativa de un job. El stream se cerrará
   * cuando el backend emita `cancelled` + `[DONE]`.
   */
  async requestCancel(jobId: string): Promise<void> {
    this.logger.debug(`[ExportJobTracker] requestCancel(${jobId}).`);
    try {
      const resp = await fetch(`${environment.api_service_exports}/jobs/${jobId}/cancel`, {
        method: 'POST',
        headers: this.authHeaders()
      });
      if (!resp.ok) {
        this.logger.error(`[ExportJobTracker] cancel HTTP ${resp.status} para job ${jobId}.`);
      }
    } catch (e) {
      this.logger.error(`[ExportJobTracker] cancel error de red:`, e);
    }
  }

  /**
   * Olvida un job: elimínalo del Map y cierra cualquier conexión residual.
   * Se invoca tras mostrar el Toast resumen al usuario.
   */
  forget(jobId: string): void {
    const ctrl = this.connections.get(jobId);
    if (ctrl) {
      ctrl.abort();
      this.connections.delete(jobId);
    }
    const next = new Map(this.jobsMap());
    next.delete(jobId);
    this.jobsMap.set(next);
  }

  // ==========================================================================
  // CONEXIÓN SSE + RECONEXIÓN
  // ==========================================================================

  private async openStreamWithRetry(
    jobId: string,
    resolveFinal: (state: AsyncJobState) => void
  ): Promise<void> {
    const url = `${environment.api_service_exports}/jobs/${jobId}/stream`;
    const ctrl = new AbortController();
    this.connections.set(jobId, ctrl);

    let attempt = 0;
    while (attempt < MAX_RECONNECT_ATTEMPTS) {
      try {
        const resp = await fetch(url, {
          headers: { ...this.authHeaders(), Accept: 'text/event-stream' },
          signal: ctrl.signal
        });
        if (!resp.ok || !resp.body) {
          throw new Error(`SSE HTTP ${resp.status}`);
        }
        attempt = 0;       // reset al conectar con éxito
        await this.consumeStream(resp.body, jobId, resolveFinal);
        return;            // consumeStream resolvió o el stream terminó limpio
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          this.logger.debug(`[ExportJobTracker] SSE abortado por el cliente (job ${jobId}).`);
          return;
        }
        attempt++;
        if (attempt >= MAX_RECONNECT_ATTEMPTS) break;
        const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * Math.pow(2, attempt - 1));
        this.logger.warn(
          `[ExportJobTracker] SSE error en job ${jobId} (intento ${attempt}/${MAX_RECONNECT_ATTEMPTS}). ` +
          `Reintentando en ${delay}ms.`, err
        );
        await sleep(delay);
      }
    }

    // Agotados los intentos — marcamos failed y resolvemos.
    this.patchJob(jobId, {
      status: 'failed',
      error: 'Conexión perdida con el servidor tras varios intentos.'
    });
    const finalState = this.jobsMap().get(jobId);
    if (finalState) resolveFinal(finalState);
    this.connections.delete(jobId);
  }

  /** Lee el ReadableStream, parsea frames `data: …\n\n` y aplica eventos. */
  private async consumeStream(
    body: ReadableStream<Uint8Array>,
    jobId: string,
    resolveFinal: (state: AsyncJobState) => void
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        const current = this.jobsMap().get(jobId);
        if (current && (current.status === 'queued' || current.status === 'running')) {
          this.patchJob(jobId, {
            status: 'failed',
            error: 'Conexión cerrada por el servidor sin marcador de fin.'
          });
        }
        const finalState = this.jobsMap().get(jobId);
        if (finalState) resolveFinal(finalState);
        this.connections.delete(jobId);
        return;
      }
      buffer += decoder.decode(value, { stream: true });

      let nl: number;
      while ((nl = buffer.indexOf('\n\n')) >= 0) {
        const block = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        const dataLine = block.split('\n').find(l => l.startsWith('data:'));
        if (!dataLine) continue;
        const data = dataLine.slice(5).trim();

        if (data === '[DONE]') {
          const finalState = this.jobsMap().get(jobId);
          if (finalState) resolveFinal(finalState);
          this.connections.delete(jobId);
          return;
        }

        try {
          this.applyEvent(jobId, JSON.parse(data));
        } catch (e) {
          this.logger.warn(`[ExportJobTracker] SSE parse error en job ${jobId}:`, data);
        }
      }
    }
  }

  /** Aplica un evento (snapshot/progress/partial/success/error/cancelled). */
  private applyEvent(jobId: string, event: { type?: string;[k: string]: unknown }): void {
    if (!event || !event.type) return;
    switch (event.type) {
      case 'snapshot': {
        const j = event['job'] as Record<string, unknown> | undefined;
        if (!j) return;
        const current = this.jobsMap().get(jobId);
        if (!current) return;
        const result = (j['result'] as Record<string, unknown>) || {};
        this.upsertJob({
          ...current,
          status: (j['status'] as JobStatus) || current.status,
          progress: typeof j['progress'] === 'number' ? (j['progress'] as number) / 100 : current.progress,
          error: (j['error'] as string) || undefined,
          externalUrl: (result['externalUrl'] as string) || undefined,
          createdCount: (result['created'] as number) ?? undefined,
          failedCount: (result['failed'] as number) ?? undefined
        });
        break;
      }
      case 'queued':
        this.patchJob(jobId, { status: 'queued' });
        break;
      case 'progress':
        this.patchJob(jobId, {
          status: 'running',
          progress: typeof event['progress'] === 'number' ? (event['progress'] as number) : 0,
          message: event['message'] as string | undefined
        });
        break;
      case 'partial':
        this.patchJob(jobId, {
          status: 'partial',
          createdCount: event['created'] as number | undefined,
          failedCount: event['failed'] as number | undefined,
          message: event['message'] as string | undefined
        });
        break;
      case 'success': {
        const summary = (event['summary'] as Record<string, unknown>) || {};
        this.patchJob(jobId, {
          status: 'success',
          progress: 1,
          createdCount: summary['created'] as number | undefined,
          failedCount: summary['failed'] as number | undefined,
          externalUrl: summary['externalUrl'] as string | undefined,
          message: summary['message'] as string | undefined
        });
        break;
      }
      case 'cancelled':
        this.patchJob(jobId, { status: 'cancelled' });
        break;
      case 'error':
        this.patchJob(jobId, {
          status: 'failed',
          error: (event['error'] as string) || 'Error desconocido en el job.'
        });
        break;
    }
  }

  // ==========================================================================
  // INTERNALS
  // ==========================================================================

  private upsertJob(state: AsyncJobState): void {
    const next = new Map(this.jobsMap());
    next.set(state.jobId, state);
    this.jobsMap.set(next);
  }

  private patchJob(jobId: string, patch: Partial<AsyncJobState>): void {
    const current = this.jobsMap().get(jobId);
    if (!current) return;
    this.upsertJob({ ...current, ...patch });
  }

  private authHeaders(): Record<string, string> {
    const token = this.auth.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}
